import React from "react";
import { useTranslation } from "react-i18next";
import {
  formatMetricValue,
  formatPercentValue,
  computeActualVsPlannedPercent,
  getDayDisplayLabel,
  getMetricDisplayLabel,
  getPercentTargetTone,
  WEEKLY_RECAP_DAY_TONES,
} from "./weeklyRecapConfig";

function getDayTone(dayIndex, isDarkMode) {
  const tone = WEEKLY_RECAP_DAY_TONES[dayIndex % WEEKLY_RECAP_DAY_TONES.length] || WEEKLY_RECAP_DAY_TONES[0];
  return isDarkMode ? tone.darkCell : tone.lightCell;
}

function getDayBadgeTone(dayIndex, isDarkMode) {
  const tone = WEEKLY_RECAP_DAY_TONES[dayIndex % WEEKLY_RECAP_DAY_TONES.length] || WEEKLY_RECAP_DAY_TONES[0];
  return isDarkMode ? tone.darkBadge : tone.lightBadge;
}

function getDayColumnTone(dayIndex, isDarkMode) {
  const tone = WEEKLY_RECAP_DAY_TONES[dayIndex % WEEKLY_RECAP_DAY_TONES.length] || WEEKLY_RECAP_DAY_TONES[0];

  const base = isDarkMode ? tone.darkCell : tone.lightCell;
  const contrast = isDarkMode
    ? "ring-1 ring-inset ring-white/5"
    : "ring-1 ring-inset ring-black/5";

  return `${base} ${contrast}`;
}

const STICKY_COLUMN_WIDTHS = {
  metric: 184,
  day: 96,
  total: 112,
  extra: 196,
};

export default function WeeklyRecapMatrixTable({
  title,
  subtitle,
  metrics,
  jDays,
  matchDayLabel,
  matrix,
  totals,
  isDarkMode,
  extraHeader,
  renderExtraCell,
  headerActions,
  isPercentMode = false,
  percentReferenceMatrix = null,
  metricLabelOverrides = {},
  dayLabelOverrides = {},
  objectiveBanner = null,
  weeklyPercentIndicator = null,
  footerRows = [],
  hideTotals = false,
}) {
  const { t } = useTranslation();
  const hasExtraColumn = Boolean(extraHeader || renderExtraCell);
  const extraRightOffset = hasExtraColumn ? STICKY_COLUMN_WIDTHS.extra : 0;
  const tableMinWidth =
    STICKY_COLUMN_WIDTHS.metric +
    jDays.length * STICKY_COLUMN_WIDTHS.day +
    (hideTotals ? 0 : STICKY_COLUMN_WIDTHS.total) +
    (hasExtraColumn ? STICKY_COLUMN_WIDTHS.extra : 0);

  const stickyShadowColor = isDarkMode ? "rgba(0, 0, 0, 0.45)" : "rgba(15, 23, 42, 0.22)";
  const metricStickyStyle = {
    left: 0,
    width: STICKY_COLUMN_WIDTHS.metric,
    minWidth: STICKY_COLUMN_WIDTHS.metric,
    maxWidth: STICKY_COLUMN_WIDTHS.metric,
    boxShadow: `8px 0 14px -14px ${stickyShadowColor}`,
  };
  const totalStickyStyle = {
    right: extraRightOffset,
    width: STICKY_COLUMN_WIDTHS.total,
    minWidth: STICKY_COLUMN_WIDTHS.total,
    maxWidth: STICKY_COLUMN_WIDTHS.total,
    boxShadow: hasExtraColumn ? "none" : `-8px 0 14px -14px ${stickyShadowColor}`,
  };
  const extraStickyStyle = {
    right: 0,
    width: STICKY_COLUMN_WIDTHS.extra,
    minWidth: STICKY_COLUMN_WIDTHS.extra,
    maxWidth: STICKY_COLUMN_WIDTHS.extra,
    boxShadow: `-8px 0 14px -14px ${stickyShadowColor}`,
  };
  const headerStickyTone = isDarkMode ? "bg-gray-900" : "bg-gray-50";

  const resolveCellPercent = (metricKey, day) => {
    const planned = percentReferenceMatrix?.[metricKey]?.[day];
    const actual = matrix?.[metricKey]?.[day];
    return computeActualVsPlannedPercent(planned, actual);
  };

  const formatCellValue = (metric, metricKey, day) => {
    if (!isPercentMode) {
      return formatMetricValue(metric, matrix?.[metricKey]?.[day]);
    }

    return formatPercentValue(resolveCellPercent(metricKey, day));
  };

  const formatTotalValue = (metric, metricKey) => formatMetricValue(metric, totals?.[metricKey]);

  return (
    <div className={`rounded-2xl border ${isDarkMode ? "border-gray-800 bg-gray-950/20" : "border-gray-200 bg-white"}`}>
      {objectiveBanner ? (
        <div
          className={`border-b px-4 py-2.5 text-xs ${
            isDarkMode
              ? "border-gray-800 bg-indigo-950/20 text-indigo-200"
              : "border-gray-150 bg-indigo-50/80 text-indigo-900"
          }`}
        >
          {objectiveBanner}
        </div>
      ) : null}
      {weeklyPercentIndicator ? (
        <div
          className={`border-b px-4 py-2 text-[11px] ${
            isDarkMode
              ? "border-gray-800 bg-gray-900/40 text-gray-400"
              : "border-gray-150 bg-gray-50 text-gray-600"
          }`}
        >
          {weeklyPercentIndicator}
        </div>
      ) : null}
      <div className={`border-b px-4 py-3.5 ${isDarkMode ? "border-gray-800" : "border-gray-150"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-wider opacity-90">{title}</h3>
            {subtitle ? (
              <p className={`mt-1 text-[11px] font-medium leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{subtitle}</p>
            ) : null}
          </div>
          {headerActions ? (
            <div className="shrink-0">{headerActions}</div>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto isolate">
        <table className="w-full border-separate border-spacing-0" style={{ minWidth: tableMinWidth }}>
          <thead className={`${isDarkMode ? "bg-gray-900/60 text-gray-200" : "bg-gray-50 text-gray-700"}`}>
            <tr className="text-xs uppercase tracking-wider font-semibold border-b border-gray-150 dark:border-gray-800">
              <th
                className={`sticky z-30 px-4 py-3 text-left font-bold ${headerStickyTone}`}
                style={metricStickyStyle}
              >
                {t("weekly_recap.columns.metric", { defaultValue: "Metric" })}
              </th>
              {jDays.map((day, dayIndex) => (
                <th
                  key={day}
                  className={`px-2 py-3 text-center border-x border-gray-150/40 dark:border-gray-800/30 ${getDayColumnTone(dayIndex, isDarkMode)}`}
                  style={{ width: STICKY_COLUMN_WIDTHS.day, minWidth: STICKY_COLUMN_WIDTHS.day }}
                >
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 font-bold normal-case text-xs shadow-sm ${getDayBadgeTone(dayIndex, isDarkMode)}`}>
                    {getDayDisplayLabel(day, dayLabelOverrides)}
                    {matchDayLabel === day ? (
                      <span className="ml-1 text-[10px] font-bold uppercase">
                        {t("weekly_recap.columns.match_short", { defaultValue: "Match" })}
                      </span>
                    ) : null}
                  </span>
                </th>
              ))}
              {!hideTotals ? (
                <th
                  className={`sticky z-30 px-4 py-3 text-center font-bold ${headerStickyTone}`}
                  style={totalStickyStyle}
                >
                  {t("weekly_recap.columns.total", { defaultValue: "Total" })}
                </th>
              ) : null}
              {hasExtraColumn ? (
                <th
                  className={`sticky z-30 px-4 py-3 text-center font-bold ${headerStickyTone}`}
                  style={extraStickyStyle}
                >
                  {extraHeader}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, metricIndex) => {
              const zebra = metricIndex % 2 === 0;
              const stickyCellTone = zebra
                ? isDarkMode
                  ? "bg-gray-950 group-hover:bg-gray-900"
                  : "bg-white group-hover:bg-cyan-50"
                : isDarkMode
                  ? "bg-gray-900 group-hover:bg-gray-900"
                  : "bg-gray-50 group-hover:bg-cyan-50";
              const stickyOpaqueTone = zebra
                ? isDarkMode ? "bg-gray-950" : "bg-white"
                : isDarkMode ? "bg-gray-900" : "bg-gray-50";

              return (
                <tr
                  key={metric.key}
                  className={`group text-sm transition-colors duration-150 hover:bg-cyan-500/5 dark:hover:bg-cyan-500/10 cursor-default border-b border-gray-100/50 dark:border-gray-800/30 ${
                    zebra
                      ? isDarkMode
                        ? "bg-gray-950/10"
                        : "bg-white"
                      : isDarkMode
                        ? "bg-gray-900/10"
                        : "bg-gray-50/40"
                  }`}
                >
                  <td
                    className={`sticky z-30 px-4 py-3 font-semibold text-xs sm:text-sm text-gray-700 dark:text-gray-300 ${stickyOpaqueTone} ${stickyCellTone}`}
                    style={metricStickyStyle}
                  >
                    <span className="block truncate">{getMetricDisplayLabel(metric, t, metricLabelOverrides)}</span>
                  </td>
                  {jDays.map((day, dayIndex) => {
                    const cellPercent = isPercentMode ? resolveCellPercent(metric.key, day) : null;

                    return (
                    <td
                      key={`${metric.key}-${day}`}
                      className={`relative z-0 px-3 py-3 text-center tabular-nums text-xs sm:text-sm border-x border-gray-100/50 dark:border-gray-800/20 font-medium whitespace-nowrap ${getDayColumnTone(dayIndex, isDarkMode)} ${isPercentMode ? getPercentTargetTone(cellPercent, isDarkMode) : ""}`}
                      style={{ width: STICKY_COLUMN_WIDTHS.day, minWidth: STICKY_COLUMN_WIDTHS.day }}
                    >
                      {formatCellValue(metric, metric.key, day)}
                    </td>
                    );
                  })}
                  {!hideTotals ? (
                    <td
                      className={`sticky z-30 px-4 py-3 text-center font-bold tabular-nums text-xs sm:text-sm whitespace-nowrap text-cyan-600 dark:text-cyan-400 ${stickyOpaqueTone} ${stickyCellTone}`}
                      style={totalStickyStyle}
                    >
                      {formatTotalValue(metric, metric.key)}
                    </td>
                  ) : null}
                  {hasExtraColumn ? (
                    <td
                      className={`sticky z-30 px-4 py-3 text-center ${stickyOpaqueTone} ${stickyCellTone}`}
                      style={extraStickyStyle}
                    >
                      {renderExtraCell
                        ? renderExtraCell({
                          metric,
                          metricKey: metric.key,
                          value: totals?.[metric.key] ?? null,
                        })
                        : null}
                    </td>
                  ) : null}
                </tr>
              );
            })}
            {(footerRows || []).map((footerRow, footerIndex) => {
              const footerMetric = footerRow?.metric;
              const footerKey = footerRow?.key || footerMetric?.key || `footer-${footerIndex}`;
              const stickyFooterTone = isDarkMode ? "bg-gray-900" : "bg-gray-50";

              return (
                <tr
                  key={footerKey}
                  className={`group border-t-2 text-sm ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-900/30"
                      : "border-gray-200 bg-gray-50/80"
                  }`}
                >
                  <td
                    className={`sticky z-30 px-4 py-3 text-xs font-bold uppercase tracking-wide ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    } ${stickyFooterTone}`}
                    style={metricStickyStyle}
                  >
                    {footerRow?.label
                      || (footerMetric
                        ? getMetricDisplayLabel(footerMetric, t, metricLabelOverrides)
                        : "")}
                  </td>
                  {jDays.map((day, dayIndex) => (
                    <td
                      key={`${footerKey}-${day}`}
                      className={`relative z-0 px-3 py-3 text-center tabular-nums text-xs sm:text-sm font-semibold whitespace-nowrap ${getDayColumnTone(dayIndex, isDarkMode)} ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                      style={{ width: STICKY_COLUMN_WIDTHS.day, minWidth: STICKY_COLUMN_WIDTHS.day }}
                    >
                      {footerRow?.formatValue
                        ? footerRow.formatValue(day)
                        : formatMetricValue(footerMetric, footerRow?.valuesByDay?.[day])}
                    </td>
                  ))}
                  {!hideTotals ? (
                    <td
                      className={`sticky z-30 px-4 py-3 text-center tabular-nums text-xs sm:text-sm ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      } ${stickyFooterTone}`}
                      style={totalStickyStyle}
                    >
                      {footerRow?.totalLabel || "—"}
                    </td>
                  ) : null}
                  {hasExtraColumn ? (
                    <td
                      className={`sticky z-30 px-4 py-3 text-center ${stickyFooterTone}`}
                      style={extraStickyStyle}
                    >
                      {footerRow?.extraLabel || "—"}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
